/**
 * PushNotificationManager - Componente per gestire le notifiche push
 * Richiede permesso, registra token, mostra notifiche in foreground
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  isPushSupported,
  getNotificationPermissionStatus,
  initializeMessaging
} from '../lib/firebase';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface PushNotificationManagerProps {
  showPrompt?: boolean;
}

const PushNotificationManager: React.FC<PushNotificationManagerProps> = ({ 
  showPrompt = true 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'unsupported'>('default');
  const [showDialog, setShowDialog] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [hasAskedBefore, setHasAskedBefore] = useState(false);

  // Check support and permission on mount
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await isPushSupported();
      setIsSupported(supported);
      
      if (supported) {
        const status = getNotificationPermissionStatus();
        setPermissionStatus(status);
        
        // Check if we've asked before
        const asked = localStorage.getItem('push_notification_asked');
        setHasAskedBefore(asked === 'true');
        
        // Initialize messaging
        await initializeMessaging();
        
        // If already granted, register token silently
        if (status === 'granted' && isAuthenticated) {
          registerPushToken();
        }
      }
    };

    checkSupport();
  }, [isAuthenticated]);

  // Show prompt dialog after delay (only if not asked before and supported)
  useEffect(() => {
    if (
      showPrompt && 
      isSupported && 
      isAuthenticated && 
      permissionStatus === 'default' && 
      !hasAskedBefore
    ) {
      const timer = setTimeout(() => {
        setShowDialog(true);
      }, 5000); // Show after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showPrompt, isSupported, isAuthenticated, permissionStatus, hasAskedBefore]);

  // Listen for foreground messages
  useEffect(() => {
    if (!isSupported || permissionStatus !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast notification for foreground messages
      const title = payload.notification?.title || 'Nuova notifica';
      const body = payload.notification?.body || '';
      
      toast(title, {
        description: body,
        icon: <BellRing className="h-4 w-4 text-primary" />,
        action: payload.data?.url ? {
          label: 'Apri',
          onClick: () => {
            window.location.href = payload.data.url;
          }
        } : undefined
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isSupported, permissionStatus]);

  // Register push token with backend
  const registerPushToken = useCallback(async () => {
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await axios.post(`${API}/push/register`, {
          token,
          device_type: getDeviceType(),
          device_name: navigator.userAgent.slice(0, 100)
        });
        console.log('Push token registered successfully');
      }
    } catch (error) {
      console.error('Error registering push token:', error);
    }
  }, []);

  // Handle enable notifications
  const handleEnableNotifications = async () => {
    setIsRegistering(true);
    localStorage.setItem('push_notification_asked', 'true');
    setHasAskedBefore(true);
    
    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        // Register with backend
        await axios.post(`${API}/push/register`, {
          token,
          device_type: getDeviceType(),
          device_name: navigator.userAgent.slice(0, 100)
        });
        
        setPermissionStatus('granted');
        toast.success('Notifiche attivate!', {
          description: 'Riceverai notifiche per ordini, messaggi e altro.'
        });
      } else {
        setPermissionStatus(getNotificationPermissionStatus());
        if (Notification.permission === 'denied') {
          toast.error('Notifiche bloccate', {
            description: 'Puoi abilitarle dalle impostazioni del browser.'
          });
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Errore nell\'attivazione delle notifiche');
    } finally {
      setIsRegistering(false);
      setShowDialog(false);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    localStorage.setItem('push_notification_asked', 'true');
    setHasAskedBefore(true);
    setShowDialog(false);
  };

  // Get device type
  const getDeviceType = (): string => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Windows/.test(ua)) return 'windows';
    if (/Mac/.test(ua)) return 'mac';
    return 'web';
  };

  // Don't render if not supported or not authenticated
  if (!isSupported || !isAuthenticated) return null;

  return (
    <>
      {/* Permission Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-primary" />
              Attiva le Notifiche
            </DialogTitle>
            <DialogDescription>
              Ricevi notifiche istantanee per:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex items-center gap-3 text-sm">
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                🛒
              </div>
              <div>
                <p className="font-medium">Nuovi Ordini</p>
                <p className="text-muted-foreground text-xs">Saprai subito quando arriva un ordine</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                💬
              </div>
              <div>
                <p className="font-medium">Messaggi</p>
                <p className="text-muted-foreground text-xs">Non perdere nessuna comunicazione</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                💰
              </div>
              <div>
                <p className="font-medium">Pagamenti</p>
                <p className="text-muted-foreground text-xs">Conferme di pagamento in tempo reale</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleDismiss}>
              Non ora
            </Button>
            <Button onClick={handleEnableNotifications} disabled={isRegistering}>
              {isRegistering ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Attivazione...
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  Attiva Notifiche
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PushNotificationManager;
